import { MoneyDisplay } from '@/components/ui/MoneyDisplay';
import type { AggregatedValueItem } from '@/api/types';

interface AggregatedChartDataItem extends AggregatedValueItem {
    name: string;
    chartValue: number;
}

interface AggregatedChartTooltipProps {
    currency: string;
    isDebt: boolean;
    active?: boolean;
    payload?: Array<{
        payload: AggregatedChartDataItem;
    }>;
}

/**
 * Custom tooltip component for the aggregated value chart.
 * Displays: Period label, total value, ledger count, change from previous (amount and %).
 */
export function AggregatedChartTooltip({
    currency,
    isDebt,
    active,
    payload,
}: AggregatedChartTooltipProps) {
    if (!active || !payload || payload.length === 0 || !payload[0]) {
        return null;
    }

    const data = payload[0].payload;
    const { periodLabel, totalValue, changeFromPrevious, changePercentage, isCarriedForward } = data;

    // Determine change color
    const getChangeColor = (change: number | null) => {
        if (change === null) return '';
        // For debts, positive change means MORE debt (bad), negative means LESS debt (good)
        // For assets, positive change is good, negative is bad
        if (isDebt) {
            return change > 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400';
        }
        return change >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400';
    };

    const formatPercentage = (percent: number | null) => {
        if (percent === null) return '';
        const sign = percent >= 0 ? '+' : '';
        return `(${sign}${percent.toFixed(2)}%)`;
    };

    // Arrow indicator for change
    const arrow = changeFromPrevious !== null
        ? changeFromPrevious > 0 ? '↑' : changeFromPrevious < 0 ? '↓' : '→'
        : null;

    return (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-2 sm:p-3 text-sm">
            {/* Period Label */}
            <div className="font-medium text-gray-900 dark:text-white text-xs sm:text-sm mb-1 sm:mb-2">
                {periodLabel}
            </div>

            {/* Desktop: labeled horizontal layout */}
            <div className="hidden sm:block">
                {/* Total Value */}
                <div className="flex items-center justify-between gap-4 mb-1">
                    <span className="text-gray-500 dark:text-gray-400">Total:</span>
                    <MoneyDisplay
                        value={totalValue}
                        currency={currency}
                        size="sm"
                        className="font-semibold text-gray-900 dark:text-white"
                    />
                </div>

                {/* Change from Previous */}
                {changeFromPrevious !== null && (
                    <div className="flex items-center justify-between gap-4 mb-1">
                        <span className="text-gray-500 dark:text-gray-400">Change:</span>
                        <span className={getChangeColor(changeFromPrevious)}>
                            {changeFromPrevious >= 0 ? '+' : ''}
                            <MoneyDisplay
                                value={changeFromPrevious}
                                currency={currency}
                                size="sm"
                                className="inline"
                            />
                            {' '}{formatPercentage(changePercentage)}
                        </span>
                    </div>
                )}

                {/* Carried Forward Indicator - desktop only */}
                {isCarriedForward && (
                    <div className="mt-2 text-xs text-amber-600 dark:text-amber-400">
                        * Contains carried forward values
                    </div>
                )}
            </div>

            {/* Mobile: vertical stacked layout with labels */}
            <div className="sm:hidden space-y-1">
                <div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">Total</div>
                    <MoneyDisplay
                        value={totalValue}
                        currency={currency}
                        size="sm"
                        className="font-semibold text-gray-900 dark:text-white"
                    />
                </div>
                {changeFromPrevious !== null && (
                    <div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">Change</div>
                        <div className={`text-xs font-medium ${getChangeColor(changeFromPrevious)}`}>
                            {arrow} {formatPercentage(changePercentage)}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
